/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { Dropdown, Space, Button, Spin } from "antd";
import { CopyOutlined, ImportOutlined, MenuOutlined } from "@ant-design/icons";
// Context
import { PricerContext } from "contexts/PricerContext";
import { GlobalContext } from "contexts/GlobalContext";
import { usePricerRowActions } from "contexts/PricerRowActionsContext";
// Helpers
import {
  handleMenuItemClick,
  pushOptionToTeamRun,
} from "helpers/Pricer/actionsButtons";
import { isPricerRowActive } from "helpers/Pricer/activeStatus";
import {
  handleCopyShortHand,
  isAdmin,
  isTrader,
  resolveRowsForMenuAction,
} from "helpers/generalHelper";
import {
  resolveInitialPricingRowIdForFormulas,
  resolveSummaryIdForFormulas,
} from "helpers/Pricer/calculationFormulas";
import {
  canUsePayoutCompare,
  uniqueSummaryIdsFromRows,
} from "helpers/payoutCompareApi";
import {
  buildPushToAuditTrailOptions,
  indexPricerRowsForAuditTrail,
} from "helpers/Pricer/pushToAuditTrailTime";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
// Constants
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import { PRICER_ROW_ACTION_MODAL } from "constants/pricerRowActions";
import {
  FIELD_NAMES,
  getPricerActionMenuItems,
} from "constants/pricerTable";

// Style
import "styles/PricerAuditAgGridLayout.scss";

const ToolTipWithChildren = ({ children }) => {
  return <>{children}</>;
};
const ActionButtons = (props) => {
  const isAggData = props.node.group ? props.node.aggData : props.node.data;
  const latestPropsRef = useRef(props);
  //Context
  const {
    globalState: { dark_Theme, loginInfo },
  } = useContext(GlobalContext);
  const {
    refetchOptionRow,
    state: {
      selectedRows,
      pricerRefAgGrid,
      isCellEditing,
      isPushingToTeamRunOrAuditTrail,
      isCellLocking,
      isOptionRowLoading,
      rowEditingData,
      refetchingRowsAfterCalcDateChange,
      contextMenuAction,
      dateSelected,
      pricerTablesData,
    },
    setIsPushingToTeamRunOrAuditTrail,
    setIsCellEditing,
    isOptionRowFetching,
    setContextMenuAction,
    openPayoutModal,
    openPayoutCompareModal,
  } = useContext(PricerContext);
  const { openRowActionModal } = usePricerRowActions();
  const notTrader = !isTrader();

  useEffect(() => {
    latestPropsRef.current = props;
  }, [props]);

  const isActionButtonsDisabled =
    isAggData?.[FIELD_NAMES.SUMMARY_ID] ===
      rowEditingData?.[FIELD_NAMES.SUMMARY_ID] &&
    (isCellEditing ||
      isOptionRowFetching ||
      isCellLocking ||
      isOptionRowLoading);

  const isActionsDisabled =
    isActionButtonsDisabled ||
    refetchingRowsAfterCalcDateChange ||
    isPushingToTeamRunOrAuditTrail;

  // Row action icons act on this row only; bulk is opt-in from the menu and only when
  // this row is part of the selection.
  const rowsForMenuAction = useMemo(() => {
    const clickedRow = {
      ...isAggData,
      [FIELD_NAMES.SUMMARY_ID]:
        isAggData?.[FIELD_NAMES.SUMMARY_ID] ??
        isAggData?.[FIELD_NAMES.SUMMARY_REFERENCE_ID],
    };
    return resolveRowsForMenuAction(
      clickedRow,
      selectedRows,
      (row) => row?.[FIELD_NAMES.SUMMARY_ID]
    );
  }, [isAggData, selectedRows]);

  const uniqueSummaryCount = uniqueSummaryIdsFromRows(rowsForMenuAction).length;
  const canPayoutCompare = canUsePayoutCompare(loginInfo);
  const isAdminUser = isAdmin();

  const returnSelectedArray = useMemo(
    () =>
      getPricerActionMenuItems({
        isBulk: false,
        isActive: isPricerRowActive(isAggData),
        notTrader,
        isAdminUser,
      }).map((item) => ({
        ...item,
        disabled: isActionButtonsDisabled,
      })),
    [isAggData, notTrader, isActionButtonsDisabled, isAdminUser]
  );

  const bulkMenuItems = useMemo(
    () =>
      getPricerActionMenuItems({
        isBulk: true,
        notTrader,
        uniqueSummaryCount,
        canPayoutCompare,
        isAdminUser,
      }).map((item) => ({
        ...item,
        disabled: isActionButtonsDisabled,
      })),
    [
      notTrader,
      isActionButtonsDisabled,
      uniqueSummaryCount,
      canPayoutCompare,
      isAdminUser,
    ]
  );

  const summaryIdsForAuditTrail = useMemo(() => {
    if (rowsForMenuAction.length < 2) return undefined;
    const ids = rowsForMenuAction.map((r) => r[FIELD_NAMES.SUMMARY_ID]);
    const gridApi = pricerRefAgGrid?.api;
    if (!gridApi || ids.length < 2) return ids;
    const orderMap = new Map();
    let idx = 0;
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      const data = node.group ? node.aggData : node.data;
      const summaryId = data?.[FIELD_NAMES.SUMMARY_ID];
      if (summaryId != null) {
        orderMap.set(summaryId, idx++);
      }
    });
    return [...ids].sort(
      (a, b) => (orderMap.get(a) ?? 999999) - (orderMap.get(b) ?? 999999)
    );
  }, [rowsForMenuAction, pricerRefAgGrid]);

  const onPushToAuditTrailClick = () => {
    const gridNodes = [];
    pricerRefAgGrid?.api?.forEachNode?.((node) => {
      gridNodes.push({
        level: node.level,
        group: Boolean(node.group),
        data: node.data,
        aggData: node.aggData,
      });
    });
    setIsPushingToTeamRunOrAuditTrail(true);
    openRowActionModal(PRICER_ROW_ACTION_MODAL.PUSH_TO_AUDIT_TRAIL, {
      isAggData,
      rowData: props.node.group ? props.node.aggData : props.node.data,
      summaryId: isAggData[FIELD_NAMES.SUMMARY_ID],
      summaryIds: summaryIdsForAuditTrail,
      rows: buildPushToAuditTrailOptions(
        rowsForMenuAction,
        {
          ...indexPricerRowsForAuditTrail(gridNodes),
          orderedIds: summaryIdsForAuditTrail,
        }
      ),
    });
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_PUSH_TO_AUDIT_TRAIL_MENU,
    });
  };

  const onPushToRunClick = (fromMenu = false) =>
    pushOptionToTeamRun({
      summaryId: isAggData[FIELD_NAMES.SUMMARY_ID],
      productId: isAggData?.["product_id"],
      fromMenu,
      openRowActionModal,
      setIsPushingToTeamRunOrAuditTrail,
    });

  const getShowFormulasOpenPayload = () => {
    const summaryId = resolveSummaryIdForFormulas(isAggData);
    return {
      summaryId,
      calculationDate: dateSelected,
      initialPricingRowId: resolveInitialPricingRowIdForFormulas(
        pricerRefAgGrid?.api,
        summaryId,
        rowEditingData,
        pricerTablesData
      ),
    };
  };

  const menuActions = {
    refetchOptionRow,
    setIsCellEditing,
    openRowActionModal,
    openPayoutModal,
    openPayoutCompareModal,
    onPushToRunClick,
    onPushToAuditTrailClick,
    getShowFormulasOpenPayload,
  };

  useEffect(() => {
    if (
      !contextMenuAction ||
      contextMenuAction.summaryId !== isAggData?.[FIELD_NAMES.SUMMARY_ID] ||
      !contextMenuAction.menuKey ||
      props?.node?.level !== 0
    ) {
      return;
    }
    const menuKey = contextMenuAction.menuKey;
    const rowsForAction =
      contextMenuAction.rowsForAction ??
      (contextMenuAction.rowData ? [contextMenuAction.rowData] : selectedRows);
    setContextMenuAction(null);
    handleMenuItemClick(menuKey, rowsForAction, latestPropsRef, menuActions);
  }, [contextMenuAction]);

  const disabledClassName = (isDropdown) =>
    classnames(
      isDropdown
        ? "action-buttons__dropdown button-icon my-dropdown"
        : "button-icon",
      { "button-light-theme": !dark_Theme },
      {
        "button-icon-disabled": isActionsDisabled,
      }
    );

  const selectedRowsIDs = selectedRows?.map(
    (item) => item[FIELD_NAMES.SUMMARY_ID]
  );

  if (props?.node?.level !== 0) return <></>;

  return (
    <div className="action-buttons__container action-button_container">
      {/*Show CopyShortHand*/}
      {isActionButtonsDisabled ||
      (isCellEditing &&
        selectedRowsIDs?.includes(isAggData[FIELD_NAMES.SUMMARY_ID])) ? (
        <Space>
          <Spin size="small" />{" "}
        </Space>
      ) : (
        <>
          <ToolTipWithChildren
            title={
              !isActionButtonsDisabled &&
              ACTION_BUTTONS_NAME.COPY_SHORTHAND.label
            }
          >
            <Button
              id="copy-shorthand"
              disabled={isActionsDisabled}
              icon={<CopyOutlined />}
              onClick={() => {
                sendEvent({
                  category: CATEGORIES.PRICER,
                  context: CONTEXTS.PRICER,
                  action: ACTIONS.CLICK,
                  component: COMPONENT_NAMES.ICON_BUTTON,
                  eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_ICON,
                });
                handleCopyShortHand(
                  props.node.group
                    ? props.node.aggData?.[FIELD_NAMES.COPY_SHORTHAND]
                    : props.node.data?.[FIELD_NAMES.COPY_SHORTHAND]
                );
              }}
              className={disabledClassName(false)}
            />
          </ToolTipWithChildren>

          {/*Show Pull To Pricer*/}
          {notTrader && (
            <ToolTipWithChildren
              title={ACTION_BUTTONS_NAME.PUSH_TO_TEAM_RUN.label}
            >
              <Button
                id="push-to-team-run"
                disabled={isActionsDisabled}
                icon={<ImportOutlined className="rotate-import--icon" />}
                onClick={onPushToRunClick}
                className={disabledClassName(false)}
              />
            </ToolTipWithChildren>
          )}

          <Dropdown
            key="pricer-action-column-dropdown"
            disabled={isActionsDisabled}
            className={disabledClassName(true)}
            trigger={["click", "contextMenu"]}
            maskTransitionName="none"
            menu={{
              items:
                rowsForMenuAction.length < 2
                  ? returnSelectedArray
                  : bulkMenuItems,
              theme: dark_Theme ? "dark" : "light",

              onClick: ({ key }) =>
                handleMenuItemClick(
                  key,
                  rowsForMenuAction,
                  latestPropsRef,
                  menuActions
                ),
            }}
            overlayClassName={`pricer-action-column-dropdown ${
              dark_Theme && "menu-dropdown-dark"
            }`}
          >
            <Space>
              <MenuOutlined id="pricer-menu-items" />
            </Space>
          </Dropdown>
        </>
      )}
    </div>
  );
};

export default ActionButtons;
